// Requires: npm install jspdf jspdf-autotable
//
// Usage — generic register/report table:
//   import { generateRegisterPDF } from './components/MiniERPPdf';
//   generateRegisterPDF({
//     title: 'Sales Register',
//     subtitle: 'Think5Whys MiniERP',
//     orientation: 'landscape',                 // 'portrait' | 'landscape'
//     columns: [
//       { header: 'Date', dataKey: 'date' },
//       { header: 'Item', dataKey: 'item' },
//       { header: 'Amount', dataKey: 'amount' },
//     ],
//     rows: [{ date: '17/07/2026', item: 'Pen', amount: '560' }, ...],
//     filename: 'sales-register',
//   });
//
// Usage — single voucher slip:
//   import { generateVoucherPDF } from './components/MiniERPPdf';
//   generateVoucherPDF(voucher, { companyName: 'Think5Whys' });

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Orientation guide baked in per point 14 of the spec:
// portrait  -> Voucher, Ledger View, Bill/Memo-style single-record documents
// landscape -> Sales/Purchase/Stock registers, anything with many columns
export const ORIENTATION = {
  VOUCHER: 'portrait',
  LEDGER: 'portrait',
  REGISTER: 'landscape',
};

const BRAND = {
  primary: [8, 145, 178],   // cyan-600
  dark: [30, 41, 59],       // slate-800
  light: [241, 245, 249],   // slate-100
};

const addHeader = (doc, title, subtitle) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.dark);
  doc.rect(0, 0, pageWidth, 24, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.setFont(undefined, 'bold');
  doc.text(title, 14, 14);
  if (subtitle) {
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(subtitle, 14, 20);
  }
  doc.setTextColor(0, 0, 0);
  return 30; // y-cursor after header
};

const addFooter = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      `Generated ${new Date().toLocaleString()} • Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    );
  }
};

export const generateRegisterPDF = ({
  title,
  subtitle = 'Think5Whys MiniERP',
  orientation = ORIENTATION.REGISTER,
  columns,
  rows,
  filename,
}) => {
  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  const startY = addHeader(doc, title, subtitle);

  autoTable(doc, {
    startY,
    head: [columns.map(c => c.header)],
    body: rows.map(r => columns.map(c => (r[c.dataKey] ?? '-'))),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: BRAND.light },
    margin: { top: startY },
  });

  addFooter(doc);
  doc.save(`${filename || title.toLowerCase().replace(/\s+/g, '-')}.pdf`);
};

export const generateVoucherPDF = (voucher, { companyName = 'Think5Whys MiniERP' } = {}) => {  const doc = new jsPDF({ orientation: ORIENTATION.VOUCHER, unit: 'mm', format: 'a4' });
  const startY = addHeader(doc, `${voucher.voucherType} Voucher`, companyName);

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Voucher No: ${voucher.voucherNumber}`, 14, startY + 6);
  doc.text(`Date: ${new Date(voucher.date).toLocaleDateString()}`, 14, startY + 12);
  if (voucher.narration) {
    doc.text(`Narration: ${voucher.narration}`, 14, startY + 18, { maxWidth: 180 });
  }

  const rows = (voucher.entries || []).map(en => [
    en.ledgerId?.ledgerCode || '-',
    en.ledgerId?.name || 'Unknown Ledger',
    en.debit ? `Rs. ${en.debit}` : '',
    en.credit ? `Rs. ${en.credit}` : '',
  ]);

  autoTable(doc, {
    startY: startY + 26,
    head: [['Code', 'Ledger', 'Debit', 'Credit']],
    body: rows,
    foot: [['', 'Total', `Rs. ${voucher.totalDebit || 0}`, `Rs. ${voucher.totalCredit || 0}`]],
    styles: { fontSize: 10, cellPadding: 3 },
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: BRAND.dark, textColor: 255, fontStyle: 'bold' },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
  });

  const finalY = doc.lastAutoTable.finalY + 20;
  doc.setFontSize(9);
  doc.text('_____________________', 14, finalY);
  doc.text('Prepared By', 14, finalY + 5);
  doc.text('_____________________', 140, finalY);
  doc.text('Authorised Signatory', 140, finalY + 5);

  addFooter(doc);
  doc.save(`voucher-${voucher.voucherNumber}.pdf`);
};

// A small, receipt-sized memo for a single Counter Memo (Quick Sales/Purchase) entry.
// memoNumber is computed by the caller (Home page) from today's list position —
// no backend field, so it's cheap and always in sync with what's actually shown.
const buildMemoDoc = (transaction, memoNumber) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
  const startY = addHeader(doc, `${transaction.type} Memo`, 'Think5Whys MiniERP');

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Memo No: ${memoNumber}`, 10, startY + 6);
  doc.text(`Date: ${new Date(transaction.date).toLocaleString()}`, 10, startY + 12);

  autoTable(doc, {
    startY: startY + 18,
    head: [['Item', 'Qty', 'Unit', 'Amount']],
    body: [[
      transaction.itemName,
      transaction.qty ?? '-',
      transaction.unit || '-',
      `Rs. ${transaction.amount}`,
    ]],
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
  });

  const y = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(9);
  doc.text(`Payment: ${transaction.paymentType}${transaction.isPaid === false ? ' (Unpaid)' : ''}`, 10, y);
  if (transaction.customerName) {
    doc.text(`${transaction.type === 'Purchase' ? 'Supplier' : 'Customer'}: ${transaction.customerName}`, 10, y + 6);
  }
  if (transaction.customerMobile) {
    doc.text(`Mobile: ${transaction.customerMobile}`, 10, y + 12);
  }
  if (transaction.remarks) {
    doc.text(`Remarks: ${transaction.remarks}`, 10, y + 18, { maxWidth: 130 });
  }

  addFooter(doc);
  return doc;
};

export const generateMemoPDF = (transaction, memoNumber) => {
  const doc = buildMemoDoc(transaction, memoNumber);
  doc.save(`memo-${memoNumber.replace('/', '-')}.pdf`);
};

// Returns a File object (for navigator.share) instead of triggering a download.
export const getMemoPdfFile = (transaction, memoNumber) => {
  const doc = buildMemoDoc(transaction, memoNumber);
  const blob = doc.output('blob');
  return new File([blob], `memo-${memoNumber.replace('/', '-')}.pdf`, { type: 'application/pdf' });
};

// EDI cross-account transaction memo — shows both businesses, the agreed
// terms, lock status, and signatures if either party attached one.
// Fetches a remote image and converts it to a data URL — jsPDF's addImage
// needs actual pixel data, not a bare URL string, to embed correctly.
const loadImageAsDataUrl = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d').drawImage(img, 0, 0);
    resolve(canvas.toDataURL('image/jpeg'));
  };
  img.onerror = reject;
  img.src = url;
});

export const generateEDIMemoPDF = async (tx) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
  const startY = addHeader(doc, `Business ${tx.transactionType} Memo`, 'Think5Whys MiniERP — Local EDI');

  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(`Status: ${tx.status}${tx.lockedAt ? ' • Locked ' + new Date(tx.lockedAt).toLocaleString() : ''}`, 10, startY + 5);
  doc.text(`Date: ${new Date(tx.date).toLocaleString()}`, 10, startY + 10);
  doc.text(`From: ${tx.initiatorUserId?.name || tx.initiatorCode} (${tx.initiatorCode})`, 10, startY + 16);
  doc.text(`To: ${tx.counterpartyUserId?.name || tx.counterpartyCode} (${tx.counterpartyCode})`, 10, startY + 21);

  let bodyRows;
  if (tx.transactionType === 'Barter') {
    bodyRows = [['Given', tx.itemGiven || '-'], ['Received', tx.itemReceived || '-'], ['Est. Value', `Rs. ${tx.amount || 0}`]];
  } else {
    bodyRows = [['Item', tx.itemName || '-'], ['Qty', tx.qty ? `${tx.qty} ${tx.unit || ''}` : '-'], ['Amount', `Rs. ${tx.amount || 0}`]];
  }

  autoTable(doc, {
    startY: startY + 27,
    head: [['Field', 'Detail']],
    body: bodyRows,
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
  });

  let y = doc.lastAutoTable.finalY + 8;
  if (tx.remarks) {
    doc.text(`Remarks: ${tx.remarks}`, 10, y, { maxWidth: 130 });
    y += 10;
  }

  y += 4;
  doc.setFontSize(8);
  doc.text('Initiator Signature', 10, y);
  if (tx.initiatorSignature) {
    try {
      const img = await loadImageAsDataUrl(tx.initiatorSignature);
      doc.addImage(img, 'JPEG', 10, y + 2, 45, 18);
    } catch (e) { doc.text('(signature image unavailable)', 10, y + 8); }
  } else if (tx.initiatorSignatureWaived) {
    doc.text('Signed digitally via', 10, y + 6, { maxWidth: 55 });
    doc.text('Think5Whys MiniERP', 10, y + 11);
  }

  doc.text('Counterparty Signature', 75, y);
  if (tx.counterpartySignature) {
    try {
      const img = await loadImageAsDataUrl(tx.counterpartySignature);
      doc.addImage(img, 'JPEG', 75, y + 2, 45, 18);
    } catch (e) { doc.text('(signature image unavailable)', 75, y + 8); }
  } else if (tx.counterpartySignatureWaived) {
    doc.text('Signed digitally via', 75, y + 6, { maxWidth: 55 });
    doc.text('Think5Whys MiniERP', 75, y + 11);
  }

  addFooter(doc);
  doc.save(`edi-${tx.transactionType.toLowerCase()}-${tx._id}.pdf`);
};
